import React, { useState } from 'react';

import { showToast } from '@opendoor/bricks/complex/Toast';
import { Button, DropdownSingleSelect, Flex, Text } from '@opendoor/bricks/core';

import { OdProtosListingsData_ListingPresentationType as ListingPresentationType } from '../../__generated__/athenaGlobalTypes';
import { useListingPresentationCompleted } from '../../graphql/athena/mutations/ListingPresentationCompleted';
import { isValidDateForLwc } from '../../routes/LwcDashboard/utils';

type ListingPresentationCompletedFormProps = {
  lwcProductId: string;
};

interface ListingPresentationTypeOption {
  label: string;
  value: ListingPresentationType;
}

/** Listing presentation type options array */
export const LISTING_PRESENTATION_TYPES: ListingPresentationTypeOption[] = [
  {
    label: 'Email',
    value: ListingPresentationType.EMAIL,
  },
  {
    label: 'In person',
    value: ListingPresentationType.IN_PERSON,
  },
  {
    label: 'Phone',
    value: ListingPresentationType.PHONE,
  },
  {
    label: 'Video call',
    value: ListingPresentationType.VIDEO_CALL,
  },
];

/** Uses the attempt first contact mutation to transition the listing state and update the time */
export const ListingPresentationCompletedForm: React.FC<ListingPresentationCompletedFormProps> = ({
  lwcProductId,
}) => {
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputDate = e.target.value;
    setFormData(inputDate);
  };

  const [formData, setFormData] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const { setListingPresentationCompleted, loading, error } = useListingPresentationCompleted();

  const isValidDate = isValidDateForLwc(formData);
  const isoDate = isValidDate ? new Date(formData).toISOString() : '';

  const handleListingPresentationCompleted = async (
    date: string,
    listingPresentationType: ListingPresentationType,
  ) => {
    setListingPresentationCompleted(lwcProductId, date, listingPresentationType)
      .then(() => {
        showToast(
          'info',
          'Successfully set listing presentation completed',
          'lwc-dashboard-toast',
          {
            toastOptions: { autoClose: 3000 },
          },
        );
      })
      .catch((_) => {});
  };

  const handleOptionChange = (option: string) => {
    setSelectedOption(option);
  };

  if (error) {
    showToast('error', 'Error setting listing presentation completed', 'lwc-dashboard-toast', {
      toastOptions: { autoClose: false },
    });
  }

  return (
    <Flex flexDirection="column" gridGap="3">
      <DropdownSingleSelect
        aria-labelledby=""
        autoComplete="off"
        title="What type of listing presentation was completed?"
        placeholder="Select an option"
        selectedValue={selectedOption ? [selectedOption] : []}
        onChange={(option) => {
          if (typeof option === 'string') {
            handleOptionChange(option);
          }
        }}
        options={LISTING_PRESENTATION_TYPES}
      />
      <Text>Listing presentation completed</Text>
      <input type="date" value={formData} onChange={handleDateChange} />
      <Button
        aria-label="Submit when listing presentation completed"
        analyticsName="set-listing-presentation-completed"
        onClick={() => {
          handleListingPresentationCompleted(isoDate, selectedOption as ListingPresentationType);
        }}
        disabled={!selectedOption || !isValidDate || loading}
        loading={loading}
      >
        Listing Presentation Completed
      </Button>
    </Flex>
  );
};
